import moment from "moment";
import { ServiceStatus } from "@/enums/ServiceStatus";
import { ServiceRequest } from "@/models/ServiceRequest";
import { CalendarTypeEnum } from "@/enums/CalendarTypeEnum";
import { ResidentLogs } from "@/enums/ResidentLogs";
import { mapState } from "vuex";
import * as firebase from "firebase";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";
import { reportPeriods } from "@/consts/reportPeriods";
import { formatUrl } from "@/utils/formatUrl";

export default {
  name: "Report360View",
  props: {
    residentId: {
      type: String,
      default: null
    },
    period: {
      type: Number,
      default: 7
    },
    isPrintableMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      servicesResident: [],
      typeToLabel: CalendarTypeEnum,
      ResidentLogs: ResidentLogs,
      selectedResidentId: null,
      selectedResidentImageUrl: null,
      backRouteTitle: null,
      periods: reportPeriods,
      Logs: {
        [ResidentLogs.OpenApp]: {
          period: 7,
          items: [],
          percent: 0
        },
        [ResidentLogs.OpenFamily]: {
          period: 7,
          items: [],
          percent: 0
        },
        [ResidentLogs.OpenNews]: {
          period: 7,
          items: [],
          percent: 0
        },
        [ResidentLogs.OpenNotifications]: {
          period: 7,
          items: [],
          percent: 0
        },
        [ResidentLogs.Calls]: {
          period: 7,
          items: [],
          percent: 0
        },
        [ResidentLogs.ChatUsage]: {
          period: 7,
          items: [],
          percent: 0
        }
      },
    };
  },

  watch: {
    period(value) {
      this.period = value
      this.loadReportData(value)
    }
  },

  computed: {
    ...mapState({
      resident() {
        return this.$store.state.residentModule.currentResident;
      },
      residentLogs() {
        return this.$store.state.residentModule.residentLogs;
      },
      facility() {
        return this.$store.state.authModule.currentFacility;
      },
      selectedFacility() {
        return this.$store.state.facilityModule.selectedFacility;
      },
      additionalServices() {
        return this.$store.state.additionalServicesModule.additionalServices;
      },
      socialServices() {
        return this.$store.state.socialClubModule.socialClubs;
      },
      awaitingApprovalServiceRequests(): ServiceRequest[] {
        return this.$store.state.additionalServicesModule.currentResidentServiceRequests.filter(
          service => {
            if (service.status == ServiceStatus.AwaitingApproval) {
              return service;
            }
          }
        );
      },
      completedServiceRequests(): ServiceRequest[] {
        return this.$store.state.additionalServicesModule.currentResidentServiceRequests.filter(
          service => {
            if (service.status == ServiceStatus.Completed) {
              return service;
            }
          }
        );
      }
    }),

    myDayLogs() {
      return this.Logs[ResidentLogs.OpenApp].items;
    },
    FamilyLogs() {
      return this.Logs[ResidentLogs.OpenFamily].items;
    },
    NewsLogs() {
      return this.Logs[ResidentLogs.OpenNews].items;
    },
    NotificationsLogs() {
      return this.Logs[ResidentLogs.OpenNotifications].items;
    },
    CallsLogs() {
      return this.Logs[ResidentLogs.Calls].items;
    },
    ChatLogs() {
      return this.Logs[ResidentLogs.ChatUsage].items;
    },
  },

  async created() {

    this.selectedResidentId = this.residentId || this.$route.params.id

    await this.$store.dispatch("getResident", this.selectedResidentId);

    await this.setResidentImage(this.resident.imageUrl)

    await this.$store.dispatch("getFacility");

    await this.loadReportData(this.period)

    await this.$store.dispatch("getAdditionalServices");

    await this.$store.dispatch("getSocialClubs");

    await this.$store.dispatch("getAdditionalServiceRequestsByResident", {
      residentId: this.selectedResidentId,
      statusList: [ServiceStatus.AwaitingApproval]
    });
    
    this.$emit('report360ViewDataLoaded')
  },

  mounted() {
    this.residentServices();
  },

  methods: {
    async setResidentImage(imageUrl) {
      if (this.isPrintableMode && !this.isLocalhost) {
        this.selectedResidentImageUrl = await formatUrl(imageUrl)
        console.log('set base64', this.selectedResidentImageUrl)
      }
      else {
        this.selectedResidentImageUrl = imageUrl
        console.log('set imageUrl', this.selectedResidentImageUrl)
      }
    },

    loadReportData(period) {
      this.getResidentLogs(period,
        [
          ResidentLogs.OpenApp,
          ResidentLogs.OpenFamily,
          ResidentLogs.OpenNews,
          ResidentLogs.OpenNotifications,
          ResidentLogs.Calls,
          ResidentLogs.ResidentChatUsage,
          ResidentLogs.FamilyChatUsage,
        ]
      );
    },

    formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
      if (!this.facility) return ''

      return getFormattedDateTimeString(date, this.selectedFacility.dateFormat, addTime ? this.selectedFacility.timeFormat : null, addYears);
    },

    residentServices() {
      const services = [...this.additionalServices, ...this.socialServices];

      services.forEach((service, index) => {
        if (
          service.membersIds?.find(id => id === this.selectedResidentId) ||
          service.membersList?.find(member => member.id === this.selectedResidentId) ||
          service.members?.find(id => id === this.selectedResidentId) ||
          service.members?.find(member => member.id === this.selectedResidentId)
        ) {
          this.servicesResident.push(service);
        }
      });
    },

    groupByHour(visits) {
      const types = visits.reduce(function (totals, visit) {
        const dayOfWeek = moment(visit.start)
          .startOf("day")
          .format("LL");
        !totals[dayOfWeek]
          ? (totals[dayOfWeek] = 1)
          : (totals[dayOfWeek] = totals[dayOfWeek] + 1);
        return totals;
      }, {});
      return types;
    },

    getDailyCount(events) {
      const types = events.reduce(function (totals, event) {
        const date = moment(event.start)
          .startOf("day")
          .format("D/MM");
        !totals[date] ? (totals[date] = 1) : (totals[date] = totals[date] + 1);
        return totals;
      }, {});
      return types;
    },

    async getResidentLogs(time, eventTypes) {

      const fromDate = moment()
        .subtract(time, "days")
        .toDate();

      await firebase
        .firestore()
        .collection("residents")
        .doc(this.selectedResidentId)
        .collection("events")
        .where("facilityId", "==", this.facility)
        .where("eventDateTime", ">=", fromDate)
        .where("eventType", "in", eventTypes)
        .get()
        .then(querySnapshot => {

          const logs = querySnapshot.docs.map(log => {
            const data = log.data();
            const start = moment(data.eventDateTime.seconds * 1000).toDate();
            const extraData = JSON.parse(data.extraData.replace(/'/g, '"'));
            data.start = start;
            data.extraData = extraData;
            return {
              ...data
            };
          });

          const logsResult = logs.reduce((total, current) => {
            const key = current.eventType;
            if (total[key]) {
              total[key].push(current)
            } else {
              total[key] = [current];
            }
            return total;
          }, {});

          Object.keys(logsResult).forEach(key => {

            if (key === ResidentLogs.FamilyChatUsage || key === ResidentLogs.ResidentChatUsage) {
              key = ResidentLogs.ChatUsage
              let chatUsageItems = [];

              if (logsResult[ResidentLogs.FamilyChatUsage] && logsResult[ResidentLogs.FamilyChatUsage].length) {
                chatUsageItems = [...chatUsageItems, ...logsResult[ResidentLogs.FamilyChatUsage]]
              }

              if (logsResult[ResidentLogs.ResidentChatUsage] && logsResult[ResidentLogs.ResidentChatUsage].length) {
                chatUsageItems = [...chatUsageItems, ...logsResult[ResidentLogs.ResidentChatUsage]]
              }

              this.Logs[ResidentLogs.ChatUsage].items = chatUsageItems;
            }
            else {
              this.Logs[key].items = logsResult[key];
            }

            const arr: any = Object.values(this.groupByHour(this.Logs[key].items)).splice(-2);
            if (arr.length === 2) {
              const num1 = arr[0];
              const num2 = arr[1];
              const result = (num2 - num1) / num1 * 100;
              this.Logs[key].percent = result.toFixed(0);
            }
          });

          eventTypes.forEach(type => {
            if (!Object.keys(logsResult).includes(type) && this.Logs[type]) {
              this.Logs[type].items = [];
              this.Logs[type].percent = 0;
            }
          });
        })
    },

    async openPrintableReports360() {
      this.$router.push(`/reports/printableReportResident360/${this.selectedResidentId}`);
    },

    backToReports360() {
      if(this.$route.params.backRouteName){
        this.$router.push({name:this.$route.params.backRouteName});
      }
      else{
        this.$router.push("/reports/reports360");
      }
    },

    isLocalhost() {
      return window.location.hostname === 'localhost';
    }
  }
};